import dayjs from "dayjs";
import { useTranslations } from "use-intl";
import { z } from "zod";
import { IncidentType, UpdateIncidentInput } from "@/gql/graphql";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { IncidentRecord } from "@/app/Incidents/GraphQL/incidentRecordQuery";
import { updateIncidentMutation } from "@/app/Incidents/GraphQL/updateIncidentMutation";
import { DateTimeInput } from "@/lib/Components/Form/Inputs/DateTimeInput";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import {
  toDateTimeString,
  toDateTimeTzString,
} from "@/lib/Formatters/toDateTimeString";
import { foundAtSchema } from "@/app/Incidents/Forms/reportIncidentSchema";
import { useCanEditIncident } from "@/app/Incidents/Hooks/useCanEditIncident";

export function IncidentReportCard({ incident }: { incident: IncidentRecord }) {
  const t = useTranslations("incident");
  const canEdit = useCanEditIncident(incident);
  const { mutateAsync } = useGqlMutation(updateIncidentMutation);
  const required = t("report.required");

  const schema = z.object({
    found_at: foundAtSchema(t),
    description: z.string().trim().min(1, required).max(5000),
    follow_up_contact: z.string().trim().min(1, required).max(255),
  });

  return (
    <RecordScreenFormCard<UpdateIncidentInput>
      title={null}
      isEditable={canEdit}
      validationSchema={toFormikValidationSchema(schema)}
      initialValues={{
        found_at: toDateTimeString(dayjs.utc(incident.found_at).local()),
        type: incident.type,
        description: incident.description,
        follow_up_contact: incident.follow_up_contact ?? "",
      }}
      onSubmit={async (values) => {
        await mutateAsync({
          id: incident.id,
          input: {
            found_at: toDateTimeTzString(values.found_at),
            type: values.type,
            description: values.description,
            follow_up_contact: values.follow_up_contact,
          },
        });
      }}
    >
      <DateTimeInput
        name="found_at"
        label={t("details.foundAt")}
        className="col-span-4"
        viewNode={formatDateTimeLocal(incident.found_at)}
      />
      <SelectInput
        name="type"
        label={t("details.type")}
        className="col-span-4"
        options={[
          {
            label: "",
            options: Object.values(IncidentType).map((type) => ({
              label: t(`type.${type}`),
              value: type,
            })),
          },
        ]}
      />
      <TextAreaInput
        name="description"
        label={t("details.description")}
        className="col-span-full"
        textAreaProps={{ rows: 4, maxLength: 5000 }}
      />
      <TextInput
        name="follow_up_contact"
        label={t("details.followUpContact")}
        className="col-span-4"
      />
    </RecordScreenFormCard>
  );
}
